/**
 * Tell an artist their biography is waiting to be read.
 *
 * 512 artists hold a drafted biography and 30 have one published. The engine has been writing
 * them for months and the acceptance gate means none of them go live until the artist says the
 * words are accurate — which is right, and which is also why nothing had happened: there was no
 * way for an artist to find out a draft existed. They would have to open Easel and click into a
 * screen they had no reason to visit.
 *
 * A banner rather than an email, deliberately. Email to 512 people who never asked for it needs a
 * consent layer this system does not have yet — `email_preferences` holds zero rows, nothing
 * reads it, no message carries an unsubscribe link, and a meaningful share of these artists are
 * in the EU. This reaches everyone who signs in, today, with no such surface.
 *
 * Dismissal is per-artist and local. A biography they have chosen not to read yet is not an error
 * state, and re-nagging on every page load is how a prompt becomes furniture people stop seeing.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useBio } from '../hooks/useBio'

const dismissKey = (artistId: number | string) => `bio-draft-dismissed:${artistId}`

export function BiographyReadyBanner({ artistId }: { artistId: number | string }) {
  const { data, isLoading } = useBio()

  const [dismissed, setDismissed] = useState(() => {
    // Storage can throw in a private window or with site data blocked; a banner is not worth
    // taking the dashboard down for.
    try {
      return localStorage.getItem(dismissKey(artistId)) === '1'
    } catch {
      return false
    }
  })

  if (isLoading || dismissed || !data?.hasDraft) return null

  const chapters = data.draft.length
  // An artist with published chapters already is being offered a revision, not a first draft.
  // Saying "your biography is ready" to them would be wrong.
  const isRevision = data.published.length > 0

  return (
    <Card className="border-primary/30 bg-primary/[0.03]">
      <CardContent className="p-4 flex items-start gap-4">
        <BookOpen className="h-5 w-5 mt-0.5 shrink-0 text-primary" />

        <div className="flex-1 min-w-0">
          <p className="font-medium">
            {isRevision
              ? 'A new draft of your biography is ready to read'
              : 'Your biography is written and waiting for you'}
          </p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {chapters} {chapters === 1 ? 'chapter' : 'chapters'}, drafted from your own work and
            words. {isRevision ? 'Your current one stays live until you replace it.' : 'Nothing is public until you say it is accurate.'}
          </p>
          <Button asChild size="sm" className="mt-3">
            <Link to="/app/easel/biography">Read it</Link>
          </Button>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          title="Not now"
          aria-label="Dismiss"
          onClick={() => {
            setDismissed(true)
            try {
              localStorage.setItem(dismissKey(artistId), '1')
            } catch {
              // Dismissed for this page view only. Better than refusing to dismiss at all.
            }
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  )
}
